import { useState, useEffect, type RefObject, useMemo } from 'react';
import { graphql, useFragment, useRelayEnvironment, fetchQuery } from 'react-relay';

import { type useFavoritesItem_item$key } from './__generated__/useFavoritesItem_item.graphql.ts';
import { type useFavoritesItem_user$key } from './__generated__/useFavoritesItem_user.graphql.ts';
import { type useFavoritesItemQuery } from './__generated__/useFavoritesItemQuery.graphql.ts';

const itemFragment = graphql`
    fragment useFavoritesItem_item on Item {
        serviceId
    }
`;
const userFragment = graphql`
    fragment useFavoritesItem_user on User {
        canShowFolder
    }
`;
const useFavoritesItemQuery = graphql`
    query useFavoritesItemQuery($userId: String!) {
        viewer {
            user(userId: $userId) {
                canShowFolder
            }
        }
    }
`;

type UseFavoritesItemProps = {
    isFolderVisible: boolean;
    showFolderPlusIcon?: boolean;
    onFavorited?: () => void;
    onUnFavorited?: () => void;
    onFolderActionStart?: () => void;
    onFolderActionEnd?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

type Props = {
    item: useFavoritesItem_item$key | null | undefined;
    user: useFavoritesItem_user$key | null | undefined;
    itemId?: string;
    userId?: string;
    applyMouseEvents?: boolean;
    tileElementRef: RefObject<HTMLDivElement | null>;
};

export const useFavoritesItem = ({
    item: itemRef,
    user: userRef,
    itemId: serviceId,
    userId,
    applyMouseEvents = false,
    tileElementRef,
}: Props): UseFavoritesItemProps => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const environment = useRelayEnvironment();

    const [isMouseOver, setIsMouseOver] = useState(false);
    const [isHeartFilled, setIsHeartFilled] = useState(false);
    const [isFolderAction, setIsFolderAction] = useState(false);
    const [showFetchedUserFolder, setShowFetchedUserFolder] = useState(false);

    // In case userRef can be passed this should be a prefered method and will avoid fetching additional user query.
    // But in some cases userRef is not available (is null) so to get folder visibility permissions we do fetch a user.
    const fetchUserFolderVisibility = applyMouseEvents && userId && !userRef;

    useEffect(() => {
        if (fetchUserFolderVisibility) {
            const subscribtion = fetchQuery<useFavoritesItemQuery>(
                environment,
                useFavoritesItemQuery,
                { userId },
                { networkCacheConfig: { force: false }, fetchPolicy: 'store-or-network' }
            ).subscribe({
                next: ({ viewer }) => setShowFetchedUserFolder(!!viewer?.user?.canShowFolder),
            });

            return () => subscribtion.unsubscribe();
        }

        return () => {};
    }, [fetchUserFolderVisibility, userId, environment, setShowFetchedUserFolder]);

    // We do get folder visibility info either from user fragment or separatelly fetched user.
    const showFolder = !!user?.canShowFolder || !!showFetchedUserFolder;
    const itemId = item?.serviceId || serviceId || '';

    const isFolderVisible = useMemo(() => {
        return (isHeartFilled || showFolder) && (isMouseOver || isFolderAction);
    }, [isHeartFilled, showFolder, isMouseOver, isFolderAction]);

    return useMemo(() => {
        if (applyMouseEvents) {
            return {
                isFolderVisible,
                showFolderPlusIcon: true,
                onFavorited: () => setIsHeartFilled(true),
                onUnFavorited: () => setIsHeartFilled(false),
                onFolderActionStart: () => setIsFolderAction(true),
                onFolderActionEnd: () => setIsFolderAction(false),
                onMouseEnter: () => {
                    setIsMouseOver(true);
                    setIsHeartFilled(
                        // We check if heart icon is filled or not by data-tn attribute
                        // instead of refetching on mouse over to check if item is favorited.
                        tileElementRef?.current
                            ?.querySelector(`[id=${itemId}-heart]`)
                            ?.getAttribute('data-tn')
                            ?.includes('saved') || false
                    );
                },
                onMouseLeave: () => setIsMouseOver(false),
            };
        } else {
            return { isFolderVisible: true };
        }
    }, [applyMouseEvents, isFolderVisible, itemId, tileElementRef]);
};
